import { useEffect, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { BaseLogo } from "../../../../core/models/entities/BaseLogo"
import { useApiClient } from "../../../../data/clients/ApiClient"
import { mapToLogo } from "../../../../data/models/mappers/mapLogo"
import { useQueryClient } from "@tanstack/react-query"

export function useOpenLogo(): { result: BaseLogo | undefined, clearOpen: () => void } {
    const navigate = useNavigate()
    const location = useLocation()
    const apiClient = useApiClient()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const id = searchParams.get('open')
    const orgId = searchParams.get('orgId')
    const [result, setResult] = useState<BaseLogo | undefined>()

    useEffect(() => {
        if (id && orgId) {
            apiClient.getLogo(Number(orgId), Number(id)).then(mapToLogo).then((res) => {
                // Seed the detail cache with this COMPLETE, freshly fetched
                // GetLogo response (same key, same mapped BaseLogo shape as
                // LogoRepository.useLogo). The details popup that opens from
                // this deep link mounts its own useLogo moments later - within
                // its 15s staleTime - so without the seed the exact same
                // 1.4-3.7s GetLogo would fire a second time. Not a stub: this
                // is full server truth, so there is nothing left to "heal".
                queryClient.setQueryData(["logos", res.id], res)
                setResult(res)
            }).catch(() => {
                setResult(undefined)
            })
        } else {
            setResult(undefined)
            clearOpen()
        }
    }, [id, orgId])

    function clearOpen() {
        const queryParams = new URLSearchParams(location.search)
        queryParams.delete('open')
        queryParams.delete('orgId')
        navigate(`${location.pathname}?${queryParams}`)
    }

    if (!id || !orgId) {
        return {
            result: undefined,
            clearOpen
        };
    }

    return {
        result,
        clearOpen
    }
}